
import React from "react";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend 
} from "recharts";

interface DeviceActivityChartProps {
  timeframe: string;
}

export function DeviceActivityChart({ timeframe }: DeviceActivityChartProps) {
  // Generate IT admin-focused data based on timeframe
  const generateData = (timeframe: string) => {
    let data = [];
    if (timeframe === "daily") {
      // 24 hours data
      data = Array.from({ length: 24 }, (_, i) => {
        const hour = i.toString().padStart(2, '0') + ":00";
        // Higher security issues during work hours (8-17)
        const workHour = i >= 8 && i <= 17;
        return {
          time: hour,
          activeDevices: Math.floor(Math.random() * 150) + (workHour ? 250 : 100),
          securityAlerts: Math.floor(Math.random() * (workHour ? 8 : 3)) + (workHour ? 5 : 1),
          pendingUpdates: Math.floor(Math.random() * 15) + 10,
          adminActions: Math.floor(Math.random() * (workHour ? 20 : 5)) + (workHour ? 15 : 2),
        };
      });
    } else if (timeframe === "weekly") {
      // 7 days data with weekend pattern
      const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      data = days.map((day, i) => {
        const isWeekend = i >= 5; // Saturday and Sunday
        return {
          time: day,
          activeDevices: Math.floor(Math.random() * 100) + (isWeekend ? 150 : 300),
          securityAlerts: Math.floor(Math.random() * (isWeekend ? 5 : 15)) + (isWeekend ? 2 : 8),
          pendingUpdates: Math.floor(Math.random() * 20) + 15 + (isWeekend ? -10 : 0),
          adminActions: Math.floor(Math.random() * (isWeekend ? 10 : 30)) + (isWeekend ? 5 : 25),
        };
      });
    } else {
      // Monthly (30 days) data with weekly pattern
      data = Array.from({ length: 30 }, (_, i) => {
        const dayOfWeek = i % 7;
        const isWeekend = dayOfWeek >= 5; // Saturday and Sunday
        return {
          time: `Day ${i + 1}`,
          activeDevices: Math.floor(Math.random() * 80) + (isWeekend ? 180 : 270),
          securityAlerts: Math.floor(Math.random() * (isWeekend ? 4 : 12)) + (isWeekend ? 2 : 7),
          pendingUpdates: Math.floor(Math.random() * 15) + 10 + (i < 15 ? 10 : 0), // More pending updates in the first half of month
          adminActions: Math.floor(Math.random() * (isWeekend ? 8 : 25)) + (isWeekend ? 3 : 18),
        };
      });
    }
    return data;
  };

  const data = generateData(timeframe);
  
  // Add an accessible description based on the timeframe
  const getDescription = (timeframe: string) => {
    switch(timeframe) {
      case "daily": return "24-hour view of device activity and security events";
      case "weekly": return "7-day summary of device activity and security events";
      case "monthly": return "30-day overview of device activity and security events";
      default: return "Time-based analysis of device activity and security events";
    }
  };

  // Define the colors from the image
  const colors = {
    purple: {
      light: '#A5B4FC',  // Light Purple
      medium: '#6366F1',  // Medium Purple
      dark: '#4F46E5',    // Dark Purple
    },
    green: {
      light: '#86EFAC',   // Light Green
      medium: '#4ADE80',  // Medium Green
      dark: '#22C55E',    // Dark Green
    },
    orange: {
      light: '#FFBB80',   // Light Orange
      medium: '#FF8C66',  // Medium Orange
      dark: '#FF5A1F',    // Dark Orange
    }
  };

  return (
    <>
      <p className="text-sm text-muted-foreground mb-2">{getDescription(timeframe)}</p>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart
          data={data}
          margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 10,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
          <XAxis 
            dataKey="time" 
            tick={{ fontFamily: 'Poppins, sans-serif', fontSize: 11 }}
          />
          <YAxis
            tick={{ fontFamily: 'Poppins, sans-serif', fontSize: 11 }}
          />
          <Tooltip 
            contentStyle={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}
          />
          <Legend 
            wrapperStyle={{ fontFamily: 'Poppins, sans-serif', fontSize: 11 }}
            iconType="circle"
            iconSize={10}
          />
          <Line 
            type="monotone" 
            dataKey="activeDevices" 
            name="Active Devices" 
            stroke={colors.purple.medium} 
            strokeWidth={2}
            dot={{ fill: colors.purple.medium, r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line 
            type="monotone" 
            dataKey="securityAlerts" 
            name="Security Alerts" 
            stroke={colors.orange.medium} 
            strokeWidth={2}
            dot={{ fill: colors.orange.medium, r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line 
            type="monotone" 
            dataKey="pendingUpdates" 
            name="Pending Updates" 
            stroke={colors.green.medium} 
            strokeWidth={2}
            dot={{ fill: colors.green.medium, r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line 
            type="monotone" 
            dataKey="adminActions" 
            name="Admin Actions" 
            stroke={colors.purple.dark} 
            strokeWidth={2}
            dot={{ fill: colors.purple.dark, r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
}
